import React from "react"
import type { TFunction } from "i18next"
import type { FormDescriptionImage } from "@f2e/shared/types"
import { FieldDescription } from "@f2e/ui/components/InteractionRequestForm"

import { Label, DatePicker } from "@f2e/ui/components/base"
import { formatDateOnly, parseDateOnly } from "@f2e/ui/lib/date"
import { BaseFieldEditor, BaseFieldProps } from "./BaseFieldEditor"

export type DateField = BaseFieldProps<{
  type: "date"
  name: string
  question: string
  label: string
  placeholder: string
  required: boolean
}>

export const DateFieldEditor: React.FC<DateField> = ({
  field,
  onChange,
  disabled,
}) => {
  return (
    <BaseFieldEditor
      field={field}
      onChange={onChange}
      disabled={disabled}
      showPlaceholder={false}
    />
  )
}

export default DateFieldEditor

export const DateFieldRenderer: React.FC<{
  field: {
    question: string
    required: boolean
    helpText?: string
    descriptionImage?: FormDescriptionImage
  }
  mode: "preview" | "runtime"
  value?: string
  onChange?: (v: string) => void
  disabled?: boolean
  error?: string
}> = ({ field, mode, value, onChange, disabled, error }) => {
  const isPreview = mode === "preview"
  const [local, setLocal] = React.useState<Date | undefined>(undefined)
  const date = isPreview ? local : parseDateOnly(value)
  const handleSelect = (d?: Date) => {
    if (isPreview) setLocal(d)
    else onChange?.(d ? formatDateOnly(d) : "")
  }
  return (
    <div className="space-y-1">
      <Label className="text-xs">
        {field.question}{" "}
        {field.required ? <span className="text-red-500">*</span> : null}
      </Label>
      <FieldDescription
        helpText={field.helpText}
        descriptionImage={field.descriptionImage}
      />
      <DatePicker
        date={date}
        onSelect={handleSelect}
        placeholder="Pick a date"
        disabled={disabled}
        className={
          error ? "border-red-500 focus-visible:ring-red-500" : undefined
        }
      />
      {error ? <div className="text-xs text-red-500">{error}</div> : null}
    </div>
  )
}

export function validateDateField(
  field: { name: string; required?: boolean },
  value: unknown,
  t: TFunction
): string | undefined {
  if (!field.required) return undefined
  const str = value === undefined || value === null ? "" : String(value)
  if (str.trim() === "")
    return t("workspace.agent.basicForm.formBuilder.validation.required")
  return undefined
}
